// frame: NxbQB @ ui/dogdisk-user.pen 弹框-选择封面(浏览式,统一组件)
// 5 个场景共用:文件夹封面 / 视频封面 / 音频封面 / 文章封面 / 资源包封面。
// defaultFolderId 决定默认打开目录(文件夹=当前 id;视频/音频=文件 parent_id;文章/资源包=null 根目录)。
//
// 数据:v_file_info_mine(view-first 强约束;直查 file_info 会因宽 policy 漏入别人的公开文件)。
// 仅 image/* 且 size_original ≤ 5MB 可选;选中整行回传 { id, name, thumbUrl } 由调用点持久化。
// 严格照 NxbQB:纯面包屑目录浏览,无搜索框、无含子目录开关。

import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Circle, CircleCheck, FolderOpen } from 'lucide-react'
import { cn, getSupabase } from '@dogdisk/shared'
import { type FileInfoMineRow } from '../../_lib/pack-api'
import { useFolderAncestors } from '../../_lib/use-folder-ancestors'
import { thumbUrl } from '../../_lib/thumb-url'

export const COVER_MAX_BYTES = 5 * 1024 * 1024

export type CoverPick = { id: string; name: string; thumbUrl: string | null }

type Crumb = { id: string | null; name: string }

const ROOT_CRUMB: Crumb = { id: null, name: '我的网盘' }

/** 字节格式化:null/负数→空串;<1KB→B;否则 KB/MB(数值<10 保留 1 位小数,否则整数)。 */
export function formatCoverBytes(bytes: number | null): string {
  if (bytes == null || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  const mb = kb / 1024
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`
}

async function fetchCoverBrowse(parentId: string | null): Promise<FileInfoMineRow[]> {
  const sb = getSupabase()
  let q = sb
    .from('v_file_info_mine')
    .select('id, name, type, base_file_id, thumb_url_list, size_original, mime_type, created_at')
    .order('type', { ascending: false })
    .order('name', { ascending: true })
    .limit(200)
  q = parentId === null ? q.is('parent_id', null) : q.eq('parent_id', parentId)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as unknown as FileInfoMineRow[]
}

function isPickableImage(r: FileInfoMineRow): boolean {
  return (
    (r.mime_type?.startsWith('image/') ?? false) &&
    r.size_original !== null &&
    r.size_original <= COVER_MAX_BYTES
  )
}

export function CoverPicker({
  open,
  onClose,
  currentCoverId,
  defaultFolderId = null,
  onSelect,
  onClear,
}: {
  open: boolean
  onClose: () => void
  currentCoverId: string | null
  defaultFolderId?: string | null
  onSelect: (pick: CoverPick) => void
  onClear?: () => void
}) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([ROOT_CRUMB])
  const navigatedRef = useRef(false)

  // 祖先链(根→当前)用于按 defaultFolderId 还原面包屑;空/null 时 hook 内部 disabled。
  const ancestorsQ = useFolderAncestors(defaultFolderId ?? '')

  // 关闭时复位「用户已导航」标记。
  useEffect(() => {
    if (!open) navigatedRef.current = false
  }, [open])

  // 打开且用户未手动导航时,用祖先链初始化面包屑(祖先链就绪后补全)。
  useEffect(() => {
    if (!open || navigatedRef.current) return
    const chain = ancestorsQ.data ?? []
    setCrumbs([ROOT_CRUMB, ...chain.map((a) => ({ id: a.id, name: a.name }))])
  }, [open, ancestorsQ.data])

  const parentId = crumbs[crumbs.length - 1]?.id ?? null

  const { data, isLoading } = useQuery({
    queryKey: ['cover-browse', parentId] as const,
    queryFn: () => fetchCoverBrowse(parentId),
    enabled: open,
  })

  const rows = (data ?? []).filter((r) => r.type === 'folder' || isPickableImage(r))
  const imageCount = rows.filter((r) => r.type !== 'folder').length

  const gotoFolder = (id: string, name: string) => {
    navigatedRef.current = true
    setCrumbs((prev) => [...prev, { id, name }])
  }
  const gotoCrumb = (i: number) => {
    navigatedRef.current = true
    setCrumbs((prev) => prev.slice(0, i + 1))
  }

  if (!open) return null

  return (
    <div
      data-frame-id="NxbQB"
      data-testid="NxbQB"
      role="dialog"
      aria-modal="true"
      aria-label="选择封面"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto flex w-full max-w-[402px] flex-col rounded-t-3xl bg-white"
        style={{ maxHeight: '85vh', paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-2">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>

        {/* HeaderRow:取消 / 选择封面 / 清除 */}
        <header className="flex items-center justify-between px-5 py-2">
          <button
            type="button"
            data-testid="NxbQB-cancel"
            onClick={onClose}
            className="text-[14px] text-[#9CA3AF]"
          >
            取消
          </button>
          <h2 className="text-[15px] font-semibold text-[#1A1A1A]">选择封面</h2>
          <button
            type="button"
            data-testid="NxbQB-clear"
            disabled={!onClear || !currentCoverId}
            onClick={() => {
              onClear?.()
              onClose()
            }}
            className={cn(
              'text-[14px]',
              onClear && currentCoverId ? 'text-[#FF6B6B]' : 'cursor-default text-transparent',
            )}
          >
            清除
          </button>
        </header>

        {/* 路径栏:面包屑 */}
        <div className="flex items-center gap-2 px-5 py-2">
          <nav
            data-testid="NxbQB-breadcrumb"
            className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
          >
            <FolderOpen size={15} className="shrink-0 text-[#2563EB]" />
            <span className="shrink-0 text-[12px] text-[#9CA3AF]">当前位置：</span>
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1
              return (
                <span key={c.id ?? 'root'} className="flex shrink-0 items-center gap-1">
                  {i > 0 && <span className="text-[12px] text-[#C4C9D0]">/</span>}
                  <button
                    type="button"
                    disabled={last}
                    onClick={() => gotoCrumb(i)}
                    className={cn(
                      'text-[12px]',
                      last ? 'font-medium text-[#1A1A1A]' : 'text-[#2563EB]',
                    )}
                  >
                    {i === 0 ? '根目录' : c.name}
                  </button>
                </span>
              )
            })}
          </nav>
        </div>

        {/* 行列表 */}
        <div className="min-h-[180px] flex-1 overflow-y-auto px-3 pb-2">
          {isLoading ? (
            <p className="py-10 text-center text-[13px] text-[#9CA3AF]">加载中…</p>
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-[#9CA3AF]">暂无图片</p>
          ) : (
            <ul data-testid="NxbQB-list">
              {rows.map((r) => {
                if (r.type === 'folder') {
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        data-testid={`NxbQB-folder-${r.id}`}
                        onClick={() => gotoFolder(r.id, r.name)}
                        className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left active:bg-[#F6F7F8]"
                      >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#EFF6FF]">
                          <FolderOpen size={22} className="text-[#2563EB]" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[14px] text-[#1A1A1A]">
                          {r.name}
                        </span>
                        <ChevronRight size={16} className="shrink-0 text-[#C4C9D0]" />
                      </button>
                    </li>
                  )
                }
                const active = r.id === currentCoverId
                const url = thumbUrl(r.thumb_url_list, r.base_file_id, r.name)
                const sizeText = formatCoverBytes(r.size_original)
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      data-testid={`NxbQB-row-${r.id}`}
                      onClick={() => {
                        onSelect({ id: r.id, name: r.name, thumbUrl: url || null })
                        onClose()
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left',
                        active ? 'bg-[#EFF6FF]' : 'active:bg-[#F6F7F8]',
                      )}
                    >
                      <span className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#F6F7F8]">
                        {url ? (
                          <img
                            src={url}
                            alt={r.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-[10px] text-[#9CA3AF]">
                            图
                          </span>
                        )}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[14px] text-[#1A1A1A]">{r.name}</span>
                        {sizeText && (
                          <span className="truncate text-[12px] text-[#9CA3AF]">{sizeText}</span>
                        )}
                      </span>
                      {active ? (
                        <CircleCheck size={20} className="shrink-0 text-[#2563EB]" />
                      ) : (
                        <Circle size={20} className="shrink-0 text-[#C4C9D0]" />
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* 底部统计 */}
        <p data-testid="NxbQB-stats" className="px-5 pt-2 text-center text-[12px] text-[#9CA3AF]">
          当前目录 {imageCount} 张图片 · 最大 5 MB
        </p>
      </div>
    </div>
  )
}
